import React, { useCallback, useEffect, useState } from 'react';
import * as Sentry from '@sentry/browser';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Button,
  TextField,
  DialogActions,
  DialogContent,
  InputAdornment,
  Box,
  ListItemButton,
  ListItemAvatar,
  Checkbox,
  ListItem,
  List,
  ListItemText,
  ListItemIcon,
  Typography,
  Dialog,
  DialogContentText,
  DialogTitle,
  Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {
  useAllStructuresForAddPlatformDialogQuery,
  StructureFilters,
  AllStructuresForAddPlatformDialogDocument,
  StructuresByUserGroupDocument,
  useAddUserGroupsToStructureMutation,
} from '@/__generated__/graphql';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import { FALLBACK_INSPECTION_IMAGE } from '@/constants';
import { containedButtonStyle, textButtonStyle } from '@/theme';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';

type Props = {
  open: boolean;
  selectedGroupID: string;
  handleToggle: () => void;
  groupName?: string;
};

type SubTextProps = {
  text: string | undefined;
};

const PlatformDetail = ({ text }: SubTextProps) => {
  return (
    <Typography sx={{ display: 'inline' }} component="span" variant="subtitle2" color="#6E8198">
      {text}
    </Typography>
  );
};

export const AddPlatformDialog = ({ open, selectedGroupID, handleToggle, groupName }: Props) => {
  const { user } = useAuth0();

  const [searchTerm, setSearchTerm] = useState('');
  const [unfinishedSearchTerm, setUnfinishedSearchTerm] = useState('');
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });
  const [selectedStructures, setSelectedStructures] = React.useState<string[]>([]);
  const [showNoPlatformsFound, setShowNoPlatformsFound] = useState<boolean>(false);

  const handleChecked = (value: string) => () => {
    const currentIndex = selectedStructures.indexOf(value);
    const newChecked = [...selectedStructures];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }
    setSelectedStructures(newChecked);
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearchTerm(unfinishedSearchTerm);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [setSearchTerm, unfinishedSearchTerm]);

  const { data, refetch } = useAllStructuresForAddPlatformDialogQuery({
    variables: {
      input: {
        excludeByUserGroupId: selectedGroupID,
        name: undefined,
      },
    },
  });

  const filterPlatformOptions = useCallback(async () => {
    const filterStructuresVariables: StructureFilters = {
      excludeByUserGroupId: selectedGroupID,
      name: undefined,
    };

    if (searchTerm) {
      filterStructuresVariables.name = searchTerm;
    } else {
      filterStructuresVariables.name = undefined;
    }
    refetch({
      input: {
        ...filterStructuresVariables,
      },
    });
  }, [selectedGroupID, searchTerm, refetch]);

  useEffect(() => {
    filterPlatformOptions();
  }, [filterPlatformOptions, searchTerm]);

  useEffect(() => {
    setShowNoPlatformsFound(open && data?.allStructures.length === 0);
  }, [data?.allStructures.length, open]);

  const [addStructureToUserGroupMutation] = useAddUserGroupsToStructureMutation();

  const handleCloseDialog = useCallback(() => {
    handleToggle();
    setSelectedStructures([]);
    setUnfinishedSearchTerm('');
  }, [handleToggle]);

  const handleCloseNoPlatformsDialog = useCallback(() => {
    handleToggle();
    setShowNoPlatformsFound(!showNoPlatformsFound);
  }, [handleToggle, showNoPlatformsFound]);

  const handleAddPlatforms = useCallback(() => {
    if (selectedStructures && selectedGroupID) {
      addStructureToUserGroupMutation({
        variables: {
          input: {
            userGroupIds: [selectedGroupID],
            structureIds: selectedStructures,
          },
        },
        refetchQueries: [StructuresByUserGroupDocument, AllStructuresForAddPlatformDialogDocument],
        onCompleted: (response) => {
          if (response) {
            handleCloseDialog();
            showSnackBar(`Platform has been added successfully`, 'success');
          }
        },
        onError: (error) => {
          Sentry.captureMessage(`Failed to add platform: ${error.message}`, {
            user: { sub: user?.sub },
            extra: {
              userGroupIds: selectedGroupID,
              structureIds: JSON.stringify(selectedStructures),
            },
          });
          showSnackBar(`Failed to add platform: ${error.message}`, 'error');
        },
      });
    }
  }, [
    selectedStructures,
    selectedGroupID,
    addStructureToUserGroupMutation,
    handleCloseDialog,
    showSnackBar,
    user?.sub,
  ]);

  const handleSearchTermChanged = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setUnfinishedSearchTerm(event.target.value);
    },
    [setUnfinishedSearchTerm]
  );

  if (data?.allStructures.length === 0) {
    return (
      <Dialog open={!!showNoPlatformsFound} onClose={handleCloseNoPlatformsDialog}>
        <DialogTitle>{`Adding platforms to the group ${groupName}`}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '1.4rem', color: 'primary.main' }}>
            {`All available platforms have already been added to the ${groupName} group. There are no platforms left for addition.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={handleCloseNoPlatformsDialog}
            variant="contained"
            sx={{ backgroundColor: 'primary.darkBlue' }}
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleCloseDialog}
      PaperProps={{
        sx: {
          minWidth: '50rem',
        },
      }}
    >
      <DialogTitle>Add Platforms</DialogTitle>
      <DialogContent>
        <TextField
          data-testid="search-platform-field"
          id="standard-basic"
          label="Search Platform"
          variant="standard"
          type="search"
          value={unfinishedSearchTerm}
          fullWidth
          disabled={data?.allStructures.length === 0}
          InputProps={{
            endAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          onChange={handleSearchTermChanged}
        />
      </DialogContent>
      <Box>
        <Box>
          <List
            sx={{
              height: 345,
              minHeight: 345,
              overflowY: 'auto',
            }}
          >
            {data?.allStructures.map((platform) => (
              <ListItem
                key={platform.id}
                data-testid={`list-platform-${platform.id}`}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRight: 0,
                  borderLeft: 0,
                  '&:hover': { backgroundColor: '#6582a40d' },
                }}
              >
                <ListItemButton
                  onClick={handleChecked(platform.id)}
                  sx={{ '&:hover': { background: 'none' } }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Checkbox
                      data-testid={`checkbox-${platform.id}`}
                      edge="start"
                      checked={selectedStructures.includes(platform.id)}
                      disableRipple
                    />
                  </ListItemIcon>
                  <ListItemAvatar sx={{ pr: 0 }}>
                    <img
                      alt={platform.name}
                      src={getCloudfrontUrl(platform.coverImagePath ?? FALLBACK_INSPECTION_IMAGE)}
                      onError={(event) => {
                        const reference = event.currentTarget;
                        reference.src = FALLBACK_INSPECTION_IMAGE;
                      }}
                      crossOrigin="use-credentials"
                      width="80"
                    />
                  </ListItemAvatar>
                  <ListItemText
                    sx={{ ml: '12px' }}
                    primaryTypographyProps={{
                      fontSize: '1.4rem',
                      fontWeight: '600',
                    }}
                    id={platform.id}
                    primary={platform?.name}
                    secondary={<PlatformDetail text={platform.description} />}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
        <Divider orientation="horizontal" />
        <DialogActions sx={{ pt: 2, pb: 2 }}>
          <Button onClick={handleCloseDialog} variant="text" sx={textButtonStyle}>
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            sx={containedButtonStyle}
            onClick={handleAddPlatforms}
            disabled={selectedStructures.length === 0}
            data-testid="add-platform-button"
          >
            Add platforms
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};
